/**
 * Parser contract — the pure I/O boundary every parser implements.
 *
 * A parser takes a document (PDF bytes, page images, …) and returns a
 * `CanonicalParseResult`. Backends vary — fully-local, a self-hosted model at a
 * network boundary, or a BYOK cloud VLM — but the interface is identical.
 */

import { Effect, Layer, Schema } from 'effect';

import {
  CanonicalEntityMetadataSchema,
  CanonicalParseResultSchema,
  CanonicalParserUsageSchema,
  type CanonicalParseResult,
  type CanonicalEntityMetadata,
  type CanonicalParserUsage,
} from './types';
import type { ParserHookName, HookContext } from './hooks';
import type { ParserExportResult, SnapshotPage } from './formatters';
import { bytesRef } from './parser-host';
import {
  makeParserError,
  parserErrorCause,
  runParseDocument,
  type ParserDefinition,
} from './parser-layer';

// ── Parser spec ─────────────────────────────────────────────────────

export const ParserTierSchema = Schema.Literals(['fast', 'standard', 'premium']);
export type ParserTier = typeof ParserTierSchema.Type;

export const ParserCategorySchema = Schema.Literals(['ocr', 'entity', 'vlm', 'projection']);
export type ParserCategory = typeof ParserCategorySchema.Type;

export const InputPortSchema = Schema.Literals([
  'pdf-bytes',
  'page-images',
  'nodes-json',
  'gcs-prefix',
]);
export type InputPort = typeof InputPortSchema.Type;

export const OutputPortSchema = Schema.Literals([
  'parsed-result',
  'entity-metadata',
  'projection-receipt',
]);
export type OutputPort = typeof OutputPortSchema.Type;

export interface ParserProperty {
  name: string;
  displayName: string;
  type: 'string' | 'number' | 'boolean' | 'options';
  default: unknown;
  description?: string;
  options?: Array<{ name: string; value: string | number | boolean }>;
}

export interface ParserSpec {
  id: string;
  name: string;
  version: string;
  tier: ParserTier;
  category: ParserCategory;
  requiredEnvKeys: string[];
  inputs: InputPort[];
  outputs: OutputPort[];
  properties: ParserProperty[];
  bestFor?: string[];
  supportsRegionExtract?: boolean;
  /** Lifecycle hooks this parser subscribes to. */
  hooks?: ParserHookName[];
  /** If true, the parser supports async submit/poll instead of a blocking parse(). */
  async?: boolean;
  /** Polling strategy for async parsers. */
  pollStrategy?: { initialDelayMs: number; maxDelayMs: number; backoffMultiplier: number };
}

// ── Parser input (immutable snapshot) ───────────────────────────────

export interface ParserInput {
  pdfBytes?: ArrayBuffer;
  pageImageUrls?: Record<number, string>;
  nodesJson?: string;
  gcsPrefix?: string;
  totalPages?: number;
  parameters?: Record<string, unknown>;
  context?: {
    documentId: string;
    fileName?: string;
    pageCount?: number;
    phase?: string;
  };
}

// ── Parser output ───────────────────────────────────────────────────

export type ParserUsage = CanonicalParserUsage;

const optionalMutable = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutableKey(Schema.optionalKey(schema));
const mutableArray = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutable(Schema.Array(schema));

export const ParserUsageSchema = CanonicalParserUsageSchema;

export interface ParserOutput {
  parsedResult?: CanonicalParseResult;
  entityMetadata?: Record<number, CanonicalEntityMetadata>;
  projectionReceipt?: { externalId?: string; rowsAffected?: number };
  costUsd?: number;
  durationMs: number;
  usage?: ParserUsage;
  debug?: Record<string, unknown>;
  /** Optional pre-normalization per-page blobs. The host may persist these as raw provenance. */
  rawPages?: Array<{ pageNumber: number; raw: unknown }>;
}

export const ParserOutputSchema: Schema.Codec<ParserOutput, unknown> = Schema.Struct({
  parsedResult: optionalMutable(CanonicalParseResultSchema),
  entityMetadata: optionalMutable(
    Schema.Record(Schema.NumberFromString, CanonicalEntityMetadataSchema),
  ),
  projectionReceipt: optionalMutable(
    Schema.Struct({
      externalId: optionalMutable(Schema.String),
      rowsAffected: optionalMutable(Schema.Finite),
    }),
  ),
  costUsd: optionalMutable(Schema.Finite),
  durationMs: Schema.Finite.pipe(Schema.mutableKey),
  usage: optionalMutable(ParserUsageSchema),
  debug: optionalMutable(Schema.Record(Schema.String, Schema.Unknown)),
  rawPages: optionalMutable(
    mutableArray(
      Schema.Struct({
        pageNumber: Schema.Finite.pipe(Schema.mutableKey),
        raw: Schema.Unknown.pipe(Schema.mutableKey),
      }),
    ),
  ),
});

// ── Async parser job types ──────────────────────────────────────────

export interface ParserJobHandle {
  /** The backend's own job id (e.g. the LlamaParse/HF job id). */
  jobId: string;
  /** The parser id that owns this job. */
  parserId: string;
  submittedAt: number;
  metadata?: Record<string, unknown>;
}

export interface ParserPollResult {
  done: boolean;
  output?: ParserOutput;
  retryAfterMs?: number;
}

// ── Parser interface ────────────────────────────────────────────────

/** @deprecated Author a `ParserDefinition` and derive this Promise surface. */
export interface Parser {
  spec: ParserSpec;
  validate(env: Record<string, unknown>): boolean;
  parse(input: ParserInput, env: Record<string, unknown>): Promise<ParserOutput>;
  /** Async submit — kick off a parser job without polling. */
  submit?(input: ParserInput, env: Record<string, unknown>): Promise<ParserJobHandle>;
  /** Async poll — check a single time whether the parser job is done. */
  poll?(handle: ParserJobHandle, env: Record<string, unknown>): Promise<ParserPollResult>;
  /** Called when a subscribed lifecycle hook fires. */
  onHook?(ctx: HookContext, env: Record<string, unknown>): Promise<void>;
  /** Transform canonical blocks to export formats. Default formatter used if absent. */
  export?(pages: SnapshotPage[]): ParserExportResult;
  /**
   * Resolve a raw parser-specific type string to a canonical BlockType for
   * rendering/export. Each parser owns its own type vocabulary — this maps it at
   * the boundary. Return undefined to fall through to the default resolver.
   */
  resolveType?(rawType: string): import('./types').BlockType | undefined;
}

/** Typed failure used by Effect-native parser implementations. */
export class ParserExecutionError extends Schema.TaggedError<ParserExecutionError>()(
  'ParserExecutionError',
  {
    parserId: Schema.String,
    operation: Schema.Literals(['parse', 'submit', 'poll', 'hook']),
    message: Schema.String,
    cause: Schema.optionalKey(Schema.Defect()),
  },
) {}

/**
 * Effect-native parser boundary. Published Promise methods are derived from
 * these programs by {@link defineEffectParser}, so callers can migrate without
 * maintaining two implementations of the parser transport.
 */
export interface EffectParser<E = ParserExecutionError> extends Omit<
  Parser,
  'parse' | 'submit' | 'poll'
> {
  parseEffect(input: ParserInput, env: Record<string, unknown>): Effect.Effect<ParserOutput, E>;
  submitEffect?(
    input: ParserInput,
    env: Record<string, unknown>,
  ): Effect.Effect<ParserJobHandle, E>;
  pollEffect?(
    handle: ParserJobHandle,
    env: Record<string, unknown>,
  ): Effect.Effect<ParserPollResult, E>;
}

export type DefinedEffectParser<E = ParserExecutionError> = Parser & EffectParser<E>;

// ── defineParser helper ─────────────────────────────────────────────

/**
 * Author a {@link Parser} with type inference and a default `validate`.
 * @deprecated Author a `ParserDefinition` and use `promiseParserFromDefinition`.
 *
 * `validate` defaults to always-available (right for fully-local parsers that
 * need no env). Provide it to gate on required keys —
 * e.g. `validate: (env) => !!env.GOOGLE_API_KEY`.
 *
 * @example
 * export const myParser = defineParser({
 *   spec: {
 *     id: 'my-parser', name: 'My Parser', version: '1.0.0',
 *     tier: 'standard', category: 'vlm', requiredEnvKeys: ['MY_KEY'],
 *     inputs: ['pdf-bytes'], outputs: ['parsed-result'], properties: [],
 *   },
 *   validate: (env) => !!env.MY_KEY,
 *   async parse(input, env) {
 *     // … call your backend, map to CanonicalParseResult …
 *     return { parsedResult, durationMs };
 *   },
 * });
 */
export function defineParser(
  def: Omit<Parser, 'validate'> & { validate?: Parser['validate'] },
): Parser {
  return { validate: () => true, ...def };
}

/**
 * Derive the stable Promise `Parser` surface from the one authoring contract.
 *
 * This is the compatibility adapter for callers that consume the established
 * Promise interface. New parser implementations still author only a definition.
 */
export function promiseParserFromDefinition<R>(
  definition: ParserDefinition<R>,
  host: Layer.Layer<R>,
): Parser {
  return {
    spec: definition.spec,
    validate: () => {
      try {
        return Effect.runSync(definition.available.pipe(Effect.provide(host)));
      } catch {
        return false;
      }
    },
    parse: (input) => {
      const program = input.pdfBytes
        ? runParseDocument(
            definition,
            {
              document: bytesRef({
                documentId: input.context?.documentId ?? `legacy:${definition.spec.id}`,
                bytes: new Uint8Array(input.pdfBytes),
                ...(input.context?.fileName === undefined
                  ? {}
                  : { fileName: input.context.fileName }),
                mimeType: 'application/pdf',
                ...((input.totalPages ?? input.context?.pageCount) === undefined
                  ? {}
                  : { pageCount: input.totalPages ?? input.context?.pageCount }),
              }),
              ...(input.parameters === undefined ? {} : { options: input.parameters }),
            },
            host,
          ).pipe(
            Effect.map(
              (result): ParserOutput => ({
                parsedResult: result,
                durationMs: result.metadata.durationMs,
                ...(result.metadata.usage?.costUsd === undefined
                  ? {}
                  : { costUsd: result.metadata.usage.costUsd }),
                ...(result.metadata.usage === undefined ? {} : { usage: result.metadata.usage }),
                ...(result.rawPages === undefined
                  ? {}
                  : {
                      rawPages: result.rawPages.map((raw, index) => ({
                        pageNumber: result.pages[index]?.pageNumber ?? index + 1,
                        raw,
                      })),
                    }),
              }),
            ),
          )
        : Effect.fail(
            makeParserError({
              parserId: definition.spec.id,
              code: 'missing_pdf',
              retryable: false,
              message: `${definition.spec.id} requires pdfBytes input`,
            }),
          );
      return Effect.runPromise(program.pipe(Effect.mapError(parserErrorCause)));
    },
    ...(definition.export === undefined ? {} : { export: definition.export }),
    ...(definition.resolveType === undefined ? {} : { resolveType: definition.resolveType }),
  };
}

/**
 * Author one Effect-native implementation while preserving the stable
 * Promise-based {@link Parser} interface.
 */
export function defineEffectParser<E>(
  def: Omit<EffectParser<E>, 'validate'> & { validate?: Parser['validate'] },
): DefinedEffectParser<E> {
  const effectParser = { validate: () => true, ...def } as EffectParser<E>;
  return {
    ...effectParser,
    parse: (input, env) => Effect.runPromise(effectParser.parseEffect(input, env)),
    ...(effectParser.submitEffect
      ? {
          submit: (input: ParserInput, env: Record<string, unknown>) =>
            Effect.runPromise(effectParser.submitEffect!(input, env)),
        }
      : {}),
    ...(effectParser.pollEffect
      ? {
          poll: (handle: ParserJobHandle, env: Record<string, unknown>) =>
            Effect.runPromise(effectParser.pollEffect!(handle, env)),
        }
      : {}),
  };
}
